import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ServiceIntegration, ServiceIntegrationPartner } from '../types';
import {
  getAvailablePartners,
  getExistingIntegrations,
  updateConfigStatus,
} from '@src/api/serviceIntegrations';
import { message } from 'antd';
import BigPandaLogo from '@src/icons/service-integrations/BigPanda.svg';
import DataDogLogo from '@src/icons/service-integrations/DataDog.svg';
import PagerDutyLogo from '@src/icons/service-integrations/PagerDuty.svg';
import GoogleChatLogo from '@src/icons/service-integrations/GoogleChat.svg';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';

export const useFetchServiceIntegrationsList = (enabled = true) => {
  return useQuery(
    ['service-integrations-list'],
    (): Promise<ServiceIntegration[]> =>
      getExistingIntegrations().then((response) => response.data?.data),
    {
      refetchOnWindowFocus: false,
      enabled,
      staleTime: 5 * 60 * 1000,
    },
  );
};

const getPartnersList = (partnersData: ServiceIntegrationPartner[]): any => {
  const PARTNER_MAPPING = {
    EI: {
      BigPanda: {
        logo: BigPandaLogo,
      },
      DataDog: {
        logo: DataDogLogo,
      },
      PagerDuty: {
        logo: PagerDutyLogo,
      },
      GoogleChat: {
        logo: GoogleChatLogo,
      },
    },
    APP: {
      PagerDuty: {
        logo: PagerDutyLogo,
      },
      GoogleChat: {
        logo: GoogleChatLogo,
      },
    },
  };

  const availablePartner = PARTNER_MAPPING[CURRENT_PRODUCT_MODE];

  return partnersData
    .filter((partner) => partner.name in availablePartner)
    .map((partner) => ({ ...partner, ...availablePartner[partner.name] }));
};

export const useFetchPartnersList = (enabled = true) => {
  return useQuery(
    ['service-integrations-partners-list'],
    (): Promise<ServiceIntegrationPartner[]> =>
      getAvailablePartners().then(({ data }) => getPartnersList(data?.data)),
    {
      refetchOnWindowFocus: false,
      enabled,
      staleTime: 5 * 60 * 1000,
    },
  );
};

export const useUpdateIntegrationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ configId, state }: any) => {
      const payload = {
        id: configId,
        payload: {
          configuration: {
            state: state,
          },
        },
      };
      return updateConfigStatus(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['service-integrations-list']);
      },
      onError: () => {
        message.error('There was an error Deleting. Please try again later.');
      },
    },
  );
};
